import { acceptTeamInvite, exchangeMagicToken } from 'api';
import { DISCOVER_AUTH_MODE_PARAM, DISCOVER_REFERRER } from 'constants/discover';
import { useTracking } from 'hooks/useTracking';
import { useSnackbar } from 'notistack';
import { useAppConfigContext } from 'providers/app-config';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { bannerActions } from 'stores/home/bannerStore';
import { useUserContext } from 'stores/user';
import { extractPostHogDistinctIdFromRedirect } from 'utils/authPostHogDistinctId';
import { removeCookie, setCookie } from 'utils/tokens';

export const handleFailToActivate = (navigate: NavigateFunction) => navigate('/sign-in', { replace: true });

// A same-origin path is safe to navigate() to; `//host/...` is excluded since browsers
// treat it as protocol-relative (i.e. off-origin).
const isSameOriginPath = (value: string): boolean => value.startsWith('/') && !value.startsWith('//');

/**
 * The backend's `redirect` query param encoding depth is inconsistent across flows: some
 * double-encode it (e.g. "/" arrives as "%252F"; `queryString.parse` strips one layer,
 * leaving "%2F", so one more decode is needed), others send it already single-encoded
 * (`queryString.parse` leaves a usable path directly). Since the depth can't be inferred
 * from the flow type, decode only when the parsed value isn't yet a usable same-origin
 * path — this reaches the double-encoded case while leaving already-decoded values alone,
 * so a query value that legitimately contains its own percent-encoding (e.g. %26) isn't
 * corrupted into a stray &.
 */
export const decodeRedirectUrl = (value: string): string => {
    if (isSameOriginPath(value)) return value;

    try {
        const decoded = decodeURIComponent(value);
        return isSameOriginPath(decoded) ? decoded : value;
    } catch {
        return value;
    }
};

/**
 * Pulls Discover's smuggled `discoverAuthMode` flag (see `DISCOVER_AUTH_MODE_PARAM`) off the
 * decoded redirect URL and returns the mode alongside the URL with the flag removed, so it
 * never leaks into the page the user actually lands on.
 */
const extractDiscoverAuthMode = (redirectUrl: string): { mode: string | null; redirectUrl: string } => {
    const [path, query] = redirectUrl.split('?');
    if (!query) return { mode: null, redirectUrl };

    const params = new URLSearchParams(query);
    const mode = params.get(DISCOVER_AUTH_MODE_PARAM);
    if (!mode) return { mode: null, redirectUrl };

    params.delete(DISCOVER_AUTH_MODE_PARAM);
    const cleanedQuery = params.toString();
    return { mode, redirectUrl: cleanedQuery ? `${path}?${cleanedQuery}` : path };
};

// `user` is optional because a resolved exchange is not proof of a usable payload: a malformed
// body, or an api helper that resolves with an Error instead of rejecting, both land here.
type MagicLinkResponse = { user?: { authToken: string; email?: string } };

type CommitContext = {
    cleanRedirect: string;
    discoverAuthMode: string | null;
    hasRedirectParam: boolean;
};

type PendingSwitch = { fromEmail: string; toEmail: string };

const normalizeEmail = (value: string): string => value.trim().toLowerCase();

/**
 * Drives magic-link / team-invite authentication.
 *
 * The token exchange is single-use, so it is NOT run automatically on mount:
 * email security scanners (e.g. Microsoft Safe Links) load magic links in a
 * headless browser and would consume the token before the user ever clicks.
 * Instead the consumer renders a confirmation button and calls `authenticate`
 * on an explicit user gesture, which scanners do not perform.
 *
 * A plain sign-in link doesn't reveal which account it belongs to until after
 * the exchange, so when the exchange resolves to a *different* account than
 * the one already logged in, the session swap is held back (`pendingSwitch`)
 * instead of applied immediately — the consumer must call `confirmSwitch` or
 * `cancelSwitch` on an explicit user gesture before the cookie/user store change.
 */
export const useAuthenticate = (activateNewUser?: boolean) => {
    const { user, refreshUser, setUserState, clearUser } = useUserContext();
    const { setShowHeroConfetti } = useAppConfigContext();
    const [authenticating, setAuthenticating] = React.useState<boolean>(false);
    const [failed, setFailed] = React.useState<boolean>(false);
    const [pendingSwitch, setPendingSwitch] = React.useState<PendingSwitch | null>(null);

    const { enqueueSnackbar } = useSnackbar();
    const { trackEvent } = useTracking();
    const navigate = useNavigate();
    const location = useLocation();
    const request = useRef(false);
    const pendingCommit = useRef<{ resp: MagicLinkResponse; ctx: CommitContext } | null>(null);

    const { token: magicToken, email, redirect } = queryString.parse(location.search);
    const hasToken = Boolean(magicToken);

    // Landing on the invite page without a token can't be completed — bounce to sign-in.
    // This is a navigation-only side effect and consumes no token.
    useEffect(() => {
        if (!hasToken && activateNewUser) handleFailToActivate(navigate);
    }, [hasToken, activateNewUser, navigate]);

    const showAuthFailure = useCallback(
        (message: string) => {
            enqueueSnackbar(message, { variant: 'error' });

            // Surface the normal sign-in form so the user can request a fresh link,
            // rather than leaving them stuck on the confirm screen with a dead token.
            setFailed(true);

            if (activateNewUser) handleFailToActivate(navigate);
        },
        [enqueueSnackbar, activateNewUser, navigate]
    );

    // The token exchange itself failed (expired/already-used link) — the token was never valid.
    const handleExchangeFailure = useCallback(
        () => showAuthFailure('Your magic link has expired! Please try again.'),
        [showAuthFailure]
    );

    // The exchange already succeeded; the failure happened while committing that result (e.g. a
    // refreshUser hiccup). "Expired link" would be misleading here — the link worked.
    const handleCommitFailure = useCallback(
        () => showAuthFailure('Something went wrong signing you in. Please try again.'),
        [showAuthFailure]
    );

    const commitAuth = useCallback(
        (resp: MagicLinkResponse, ctx: CommitContext) => {
            // Reject rather than let the property access throw: a synchronous throw here happens
            // before this function returns a promise, so the caller's `.catch(handleCommitFailure)`
            // never attaches and the error escapes unhandled — no message, just a dead screen.
            if (!resp.user?.authToken) {
                return Promise.reject(new Error('Token exchange resolved without an auth token'));
            }

            // Deliberately skip clearing the previous session before this: setting an empty/falsy
            // token here would flip the top-level route guard (`user.token ? ... : <AuthBlocked/>`)
            // to its logged-out branch for the whole async gap until `refreshUser` resolves,
            // unmounting this very page and flashing the default "Finish signing in" screen.
            // `refreshUser` below already replaces `user` atomically once the new cookie resolves.
            setCookie(resp.user.authToken);
            return refreshUser()
                .then(user => {
                    setUserState(user);
                    if (!user.token) {
                        throw new Error('No user token');
                    }

                    if (ctx.discoverAuthMode === 'signup') {
                        trackEvent('signup_completed', { method: 'magic_link', referrer: DISCOVER_REFERRER });
                    }

                    bannerActions.setBannerVisibility(true);
                    setShowHeroConfetti?.(true);

                    // Skip profile redirect for booking referrer users
                    const isReferrer = Boolean(user.team?.referrer);

                    let userNeedsToFillProfile = !user.firstName || !user.lastName;
                    if (!user?.isSsoUser) {
                        userNeedsToFillProfile = userNeedsToFillProfile || !user.role || !user.phone;
                    }

                    const nextUrl = userNeedsToFillProfile && !isReferrer ? '/profile' : ctx.cleanRedirect;

                    navigate(nextUrl, {
                        state: {
                            ...(ctx.hasRedirectParam && { redirectTo: ctx.cleanRedirect }),
                            ...(activateNewUser && { firstTimeUser: true }),
                        },
                    });
                })
                .catch(error => {
                    // The cookie above was already overwritten with the new account's token, so a
                    // failure past this point can't fall back to the previous session — it's gone.
                    // Without this, the cookie (new account) and the user store (stale or missing
                    // a token) diverge, and every API call would run under the new account while
                    // the UI still shows the old one. Force a clean logout instead.
                    removeCookie();
                    clearUser();
                    throw error;
                });
        },
        [refreshUser, setUserState, clearUser, navigate, activateNewUser, trackEvent, setShowHeroConfetti]
    );

    const authenticate = useCallback(() => {
        if (request.current || !magicToken) return;

        const decodedRedirect = typeof redirect === 'string' ? decodeRedirectUrl(redirect) : '/';
        // Discover's magic-link sign-up can't run its in-modal `onAuthSuccess` callback (the
        // user completes this exchange after navigating away to click the emailed link), so it
        // stashes the mode onto the redirect URL instead — recover it here and strip it before
        // navigating so it never leaks into the destination page's URL.
        const { mode: discoverAuthMode, redirectUrl: parsedRedirect } = extractDiscoverAuthMode(decodedRedirect);
        const { distinctId: redirectDistinctId, cleanRedirect } = extractPostHogDistinctIdFromRedirect(parsedRedirect);
        const parsedEmail = Array.isArray(email) ? email[0] : email || '';

        const wasLoggedIn = Boolean(user.token);
        const previousEmail = user.email;
        const ctx: CommitContext = { cleanRedirect, discoverAuthMode, hasRedirectParam: Boolean(redirect) };

        setAuthenticating(true);
        setFailed(false);
        request.current = true;

        const promise = activateNewUser
            ? acceptTeamInvite(magicToken as string, parsedEmail as string, redirectDistinctId)
            : exchangeMagicToken(magicToken as string, redirectDistinctId);

        promise
            .then((resp: MagicLinkResponse) => {
                const newEmail = resp.user?.email;
                const isDifferentAccount =
                    wasLoggedIn &&
                    Boolean(newEmail) &&
                    normalizeEmail(newEmail as string) !== normalizeEmail(previousEmail);

                // Don't touch the cookie or user store yet — surface a confirmation instead so an
                // already-logged-in user can back out before their session actually gets replaced.
                if (isDifferentAccount) {
                    pendingCommit.current = { resp, ctx };
                    setPendingSwitch({ fromEmail: previousEmail, toEmail: newEmail as string });
                    return;
                }

                // Exchange already succeeded past this point, so a failure here is a commit
                // failure, not an expired-link failure — keep it off the outer exchange catch.
                return commitAuth(resp, ctx).catch(handleCommitFailure);
            }, handleExchangeFailure)
            .finally(() => {
                setAuthenticating(false);
                request.current = false;
            });
    }, [
        magicToken,
        email,
        redirect,
        activateNewUser,
        user.token,
        user.email,
        commitAuth,
        handleExchangeFailure,
        handleCommitFailure,
    ]);

    const confirmSwitch = useCallback(() => {
        const pending = pendingCommit.current;
        if (!pending) return;

        pendingCommit.current = null;
        setAuthenticating(true);

        commitAuth(pending.resp, pending.ctx)
            .catch(handleCommitFailure)
            .finally(() => {
                setAuthenticating(false);
                // Cleared only once the commit settles, not eagerly: nulling this up front drops
                // the tree from AccountSwitchConfirm to MagicLinkConfirm mid-commit (both `hasToken`
                // and `authenticating` still true), which only reads as fine today because the two
                // components happen to render identical "Authenticating..." copy.
                setPendingSwitch(null);
            });
    }, [commitAuth, handleCommitFailure]);

    const cancelSwitch = useCallback(() => {
        pendingCommit.current = null;
        setPendingSwitch(null);
        // Not the pending redirect: it was built for the account the user just declined to
        // become, and may deep-link into that account's team (e.g. /event/456), 403/404-ing for
        // the account they're staying signed in as.
        navigate('/');
    }, [navigate]);

    return { hasToken, authenticating, authenticate, failed, pendingSwitch, confirmSwitch, cancelSwitch };
};
